<script>
import _ from 'lodash'

import {
  mapActions,
  mapGetters
} from 'vuex'

import CandelaLogo from '@/assets/svg/candela-logo.svg?inline'
import component_config from '@/components/configuration'
import Sidebar from '@/views/Sidebar'
import {registered_component_names} from '@/main'
import {gridfit, matrixize} from '@/utils/gridfit'

const COLUMN_COUNT = 12

export default {
  components: {
    CandelaLogo,
    Sidebar
  },
  computed: {
    ...mapGetters([
      'app_size',
      'components',
      'saved_app_layout',
      'user'
    ])
  },
  data() {
    return {
      drag_ignore: 'a, button, div.ignore-drag',
      layout: [],
      margins: [8, 8],
      row_height: 8,
      item_idx: 0
    }
  },
  methods: {
    /**
     * For each of component
     * update an existing grid item or create a new one
     */
    add_grid_items() {
      Object.keys(this.components).forEach(component_id => {
        const type = this.components[component_id].type
        const exists = _.find(this.layout, l => l.i === component_id)
        if(!exists) {
          const saved_layout = this.saved_app_layout[component_id]
          const config = component_config[type] ?
            component_config[type].default : component_config.base.default

          const l = {
            ...{
              w: config.w,
              h: config.h,
              x: config.x,
              y: config.y,
              idx: this.item_idx++
            },
            ...(saved_layout || {})
          }

          const has_xy = l.x !== undefined && l.y !== undefined
          // If there is not (x, y) defined for this component type
          // we can calculate the best next position
          if(!has_xy) {
            const matrix = matrixize(this.layout, COLUMN_COUNT)
            const xy = gridfit(matrix, l.w, l.h, COLUMN_COUNT)
            l.x = xy[0]
            l.y = xy[1]
          }

          const grid_item = {...l, i: component_id, type}
          this.layout.push(grid_item)
          this.update_component_layout(this.layout)
          this.save_app_layout()
        }
      })
    },
    get_component(id) { return this.components[id] },
    get_component_type(type) {
      const component_type = `candela-${type}`
      if(registered_component_names.has(component_type)) return component_type
      return {template: `<div>Unfamiliar figure type: ${component_type}</div>`}
    },
    ...mapActions([
      'save_app_layout',
      'set_app_size',
      'update_component_frame',
      'update_component_layout'
    ]),
    /**
     * Called when a component has been dragged and dropped into new location
     */
    on_component_moved(item) {
      this.update_component_layout(this.layout)
      this.save_app_layout()
    },
    /**
     * Called as a component is resizing
     */
    on_component_resize(item) {
      this.update_component_layout(this.layout)
      this.update_component_frame(item.i)
      this.save_app_layout()
    },
    /**
     * Called when a component resize is complete
     */
    on_component_resized(item) {
      this.update_component_layout(this.layout)
      this.update_component_frame(item.i)
      this.save_app_layout()
    },
    /**
     * Remove items from layout that are no longer in components list
     */
    prune_layout() {
      const removed = this.layout
        .filter(l => !this.components[l.i])
        .map(l => l.i)
      this.layout = this.layout.filter(l => !removed.includes(l.i))
    }
  },
  name: 'Dashboard',
  watch: {
    components() {
      this.prune_layout()
      this.add_grid_items()
    }
  }
}
</script>

<template>
<v-content>
  <v-container fluid class='flex-grow-1 pa-0 ma-0' :style='{height: `100%`}'>
    <grid-layout
      v-if='layout.length'
      :breakpoints='$vuetify.thresholds'
      :margin='margins'
      :is-draggable='true'
      :is-resizable='true'
      :layout.sync='layout'
      :responsive='false'
      :row-height='row_height'
      :use-css-transforms='true'
      :vertical-compact='true'>

      <grid-item v-for='li in layout'
        :key='li.i' :x='li.x' :y='li.y'
        :id='`${li.i}-grid-item`'
        :w='li.w' :h='li.h' :i='li.i'
        :drag-ignore-from='drag_ignore'
        @moved='on_component_moved(li)'
        @resize='on_component_resize(li)'
        @resized='on_component_resized(li)'>
        <div class='component-outer' v-if='get_component(li.i)'
          :highlight='get_component(li.i).highlight'>
          <div class='component-inner'>
            <v-card class='d-flex flex-column'
              :loading='get_component(li.i).loading'>
              <div :ref='`${li.i}-title`' :highlight='get_component(li.i).highlight'
                class='d-flex align-center
                  justify-center component-title-container px-4 py-1'>
                <div class='subtitle component-title font-weight-regular'>
                  {{get_component(li.i).title}}
                </div>
              </div>
              <div class='d-flex flex-grow-1 flex-shrink-1 flex-column pa-3
                overflow-auto ignore-drag'>
                <component :is='get_component_type(get_component(li.i).type)'
                  v-bind='get_component(li.i)'/>
                <div v-if='get_component(li.i).controls'
                  :class='[
                    `d-flex flex-row flex-wrap`, `candela-compoent`,
                    get_component(li.i).type === `control-panel` ? `flex-grow-1` : null,
                    get_component(li.i).flow === `column` ? `flex-column` : `flex-row`]'>
                  <component v-for='[id, control] in
                    Object.entries(get_component(li.i).controls)'
                    :key='id' :is='get_component_type(control.type)'
                    v-bind='control' :id='id' :component_id='li.i'
                    class='d-flex justify-center pa-2
                      ma-1 control-item flex-grow-1'/>
                </div>
              </div>
            </v-card>
          </div>
        </div>
      </grid-item>
    </grid-layout>
    <div v-else
      class='d-flex title justify-center align-center primary--text'
      :style='{height: `100%`}'>
      <div v-if='user' class='d-flex flex-column align-center'>
        <CandelaLogo width='128px' class='waiting-logo pb-6'/>
      </div>
    </div>
  </v-container>
  <Sidebar/>
</v-content>
</template>

<style lang='sass' scoped>
.container
  .waiting-logo
    animation: pulse 6s infinite

  @keyframes pulse
    0%
      transform: scale(0.90)
      opacity: 0.5
      animation-timing-function: ease-out

    50%
      transform: scale(1)
      opacity: 1.0
      animation-timing-function: ease-in

    100%
      transform: scale(0.90)
      opacity: 0.5
      animation-timing-function: ease-out

  .vue-grid-item
    .component-outer[highlight=true]
      border: 1px solid var(--v-secondary-base)

    .component-outer
      height: 100%

    .component-inner
      height: 100%
      width: 100%
      overflow: hidden

      .v-sheet
        height: 100%

      .candela-compoent
        max-height: 100%

      .component-title-container
        flex-wrap: nowrap
        min-height: 32px

        .component-settings-btn-container
          position: absolute
          right: 0

          .component-settings-btn
            justify-self: end

        .component-title
          overflow: hidden
          text-overflow: ellipsis
          white-space: nowrap

        .control-item
          border-radius: 1em

        .control-item:last-child
          margin-right: 0

      .component-title-container[highlight=true]
        transition: background-color 1s
        background-color: var(--v-secondary-base)

.theme--dark
  .control-item
    background-color: var(--v-grey-darken8)

  .component-outer
    border: 1px solid transparent

  .component-title-container
    background-color: var(--v-grey-darken8)
    border-bottom: 1px solid var(--v-grey-darken8)

.theme--light
  .control-item
    background-color: var(--v-grey-lighten9)
    border: 1px solid var(--v-grey-lighten8)

  .component-outer
    border: 1px solid var(--v-grey-base)

  .component-title-container
    background-color: var(--v-white-base)
    border-bottom: 1px solid var(--v-grey-lighten6) !important
</style>

<style lang='sass'>
.vue-grid-item
  .vue-resizable-handle
    width: 24px !important
    height: 24px !important
    margin-bottom: 2px
    margin-right: 2px
    background-repeat: no-repeat
    background: url('data:image/svg+xml;utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="lightgrey" d="M22,22H20V20H22V22M22,18H20V16H22V18M18,22H16V20H18V22M18,18H16V16H18V18M14,22H12V20H14V22M22,14H20V12H22V14Z"></path></svg>') !important
</style>
